/**
 * Envío LMX-T · secciones del canvas (recuadros con nombre). Sólo visuales:
 * viven aparte del flujo (no son nodos del proyecto), así nunca afectan la
 * validación, la ejecución ni el guardado del flujo.
 */
import { create } from 'zustand';
import axios from 'axios';
import { SECTION_COLORS, sectionMoves } from '../lmx/trabajos';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SAVE_MS = 450;
const timers = {};
const pendingPatch = {};

export const useSectionsStore = create((set, get) => ({
  projectId: null,
  sections: [],

  load: async (projectId) => {
    if (!projectId) { set({ projectId: null, sections: [] }); return; }
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}/sections`);
      set({ projectId, sections: data?.sections || [] });
    } catch {
      // Sin migración o sin red: el canvas funciona igual, sin recuadros.
      set({ projectId, sections: [] });
    }
  },

  create: async (box, name) => {
    const { projectId, sections } = get();
    if (!projectId) return null;
    const color = SECTION_COLORS[sections.length % SECTION_COLORS.length];
    try {
      const { data } = await axios.post(`${API}/projects/${projectId}/sections`,
        { name: name || 'Sección', color, ...box });
      if (data?.section) set((s) => ({ sections: [...s.sections, data.section] }));
      return data?.section || null;
    } catch (e) {
      throw new Error(e?.response?.data?.detail || 'No se pudo crear la sección');
    }
  },

  patchLocal: (id, patch) => set((s) => ({
    sections: s.sections.map((x) => (x.id === id ? { ...x, ...patch } : x)),
  })),

  /** Guarda con un pequeño retraso (arrastrar/agrandar manda un solo pedido). */
  save: (id, patch) => {
    pendingPatch[id] = { ...(pendingPatch[id] || {}), ...patch };
    clearTimeout(timers[id]);
    timers[id] = setTimeout(() => {
      const body = pendingPatch[id];
      delete pendingPatch[id];
      axios.patch(`${API}/sections/${id}`, body).catch(() => { /* reintenta al próximo cambio */ });
    }, SAVE_MS);
  },

  update: (id, patch) => { get().patchLocal(id, patch); get().save(id, patch); },

  remove: async (id) => {
    const before = get().sections;
    set({ sections: before.filter((x) => x.id !== id) });
    try { await axios.delete(`${API}/sections/${id}`); } catch { set({ sections: before }); }
  },
}));

/** Aplica los cambios de mover secciones que manda React Flow. */
export const applySectionMoves = (changes) => {
  const st = useSectionsStore.getState();
  sectionMoves(changes).forEach(({ id, patch, save }) => {
    if (patch) st.patchLocal(id, patch);
    if (save) {
      const cur = useSectionsStore.getState().sections.find((x) => x.id === id);
      if (cur) st.save(id, { x: cur.x, y: cur.y });
    }
  });
};

export default useSectionsStore;
